import { DateRangePicker } from '@modules/common/components/DateRangePicker/DateRangePicker';
import {
  formatDateRange,
  getDateRange,
} from '@modules/common/components/DateRangePicker/helpers';
import { useDatePresets } from '@modules/common/components/DateRangePicker/hooks';
import { Preset } from '@modules/common/components/DateRangePicker/hooks/useDatePresets';
import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import {
  formatDateTimeRange,
  isSinceDateTimeRange,
} from '@modules/common/utils';
import {
  DASHBOARD_QUICK_FILTER_OPTIONS,
  DEFAULT_DATE_PRESET,
} from '@modules/dashboard/components/GlobalFilters/constants';
import { trackDashboardGlobalFilterEvents } from '@modules/dashboard/helpers';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonProps,
  Tooltip,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';

interface DurationFieldProps {
  isReadOnly: boolean;
  buttonColor: ToggleButtonProps['color'];
}

export function DurationField({ isReadOnly, buttonColor }: DurationFieldProps) {
  const { duration, dispatch } = useDashboardStore(
    ({ query: { duration }, dispatch }) => ({
      duration,
      dispatch,
    }),
    shallow,
  );

  const datePresets = useDatePresets(true, DASHBOARD_QUICK_FILTER_OPTIONS);

  const renderDefaultPreset = useCallback(() => {
    return (
      <ToggleButton
        value="default"
        key="default"
        selected={duration === undefined}
        onClick={() => {
          dispatch({
            type: DashboardActionType.SET_QUERY,
            payload: { duration: undefined },
          });
        }}
        color={buttonColor}
      >
        <Tooltip
          title="Date range set in the respective Quantify"
          arrow
          placement="top"
        >
          <Box component="span">Default</Box>
        </Tooltip>
      </ToggleButton>
    );
  }, [dispatch, duration, buttonColor]);

  const globalFilterDatePresets: Preset[] = useMemo(() => {
    const defaultPresetStartAfter = isSinceDateTimeRange(
      DEFAULT_DATE_PRESET.timeRange,
    )
      ? DEFAULT_DATE_PRESET.timeRange.startAfter
      : undefined;

    return [
      {
        ...DEFAULT_DATE_PRESET,
        timeRange: {
          type: DEFAULT_DATE_PRESET.timeRange.type,
          startAfter: defaultPresetStartAfter,
        },
        dateRangeString: '',
        renderPreset: renderDefaultPreset,
      } as Preset,
      ...datePresets,
    ];
  }, [datePresets, renderDefaultPreset]);

  function onDurationchange(newDuration: DateTimeRange) {
    const [startDate, endDate] = getDateRange(newDuration);

    dispatch({
      type: DashboardActionType.SET_QUERY,
      payload: {
        duration: newDuration,
      },
    });

    const isDurationSetToDefault =
      startDate &&
      endDate &&
      formatDateRange(startDate, endDate) ===
        DEFAULT_DATE_PRESET.dateRangeString;

    trackDashboardGlobalFilterEvents({
      eventName: 'Dashboard Global Filter - DateRange Changed',
      query: {
        duration: isDurationSetToDefault
          ? 'Default'
          : formatDateTimeRange(newDuration),
      },
    });
  }

  return (
    <Stack spacing={1} direction="row" alignItems="center">
      <CalendarTodayRoundedIcon
        fontSize="small"
        sx={{
          color: 'text.secondary',
        }}
      />
      <DateRangePicker
        isReadOnly={isReadOnly}
        timeRange={duration as DateTimeRange}
        onChange={onDurationchange}
        datePresets={globalFilterDatePresets}
        toggleButtonColor={buttonColor}
      />
    </Stack>
  );
}
